/*
 * File upload pipeline.
 *
 * Every entry point takes the caller's files plus a TypeUploadConfig and returns
 * `{ promise, controller }`. The promise resolves to a TypeUploadResult — the
 * success/error/skipped buckets and an `aborted` flag — so upload failures land in
 * `error` instead of rejecting. The controller drives the whole run: abort, pause,
 * resume, getState.
 *
 * There are two shapes of upload, not four modes: one request per file (`sendUpload`) and one
 * request for every file (`sendBatchFiles`). Everything that used to distinguish parallel from
 * limited from sequential is now a number in the `concurrency` block.
 *
 * Layers, outermost first:
 *
 *   sendUpload(files, config)        one request per file      <- the entry point
 *   sendBatchFiles(files, config)    one request for all files
 *       createUploadContext          abort/pause flags, counters, byte totals
 *       uploadFiles                  concurrency + result buckets  <- the engine
 *         resolveUploadConfig        caller config -> one block per feature
 *         createFeatures             builds only the features their block enables
 *         createUploadProgress       byte-weighted, throttled progress events
 *         createRequestRegistry      tracks live XHRs so abort can reach them
 *         composeAttempt             nests the features around one attempt
 *           uploadOnceFile           body building only
 *             prepareUploadRequest   fnPrepareFile/fnBody -> body + byte count
 *             sendRequest            one XHR, one attempt  <- the transport
 *         classifyOutcome            last result -> ok | aborted | error
 *       createUploadController       handed back to the caller
 *
 * Features (`core/`, `features/`):
 *
 * Everything optional lives in `features/`, one file each, and is switched on by its own config
 * block — `retry: { enabled: true, max: 3 }`, `stall: { enabled: true, timeoutMs: 30000 }`. Each
 * factory returns null when its block is off, and the pipeline is correct with none of them built:
 * that is the property to preserve when adding one. The flat settings that predate the blocks
 * (`maxRetries`, `stallTimeoutMs`, `concurrency`, …) still work — `resolveUploadConfig` maps them.
 *
 * A feature plugs in through one of three extension points, because the three jobs are different
 * enough that no single one covers them:
 *
 *   fnShouldSend  decides whether a file is worth sending at all, before there is an attempt to
 *                 wrap or a request to watch. `filter` is this. A file turned down here is
 *                 reported as skipped, which is neither a success nor an error.
 *   wrap          decorates the whole attempt. Retry re-runs it; chunking will fan it out. It
 *                 cannot see inside a request, only replace or repeat the call.
 *   request       observes one request from the inside, through onStart/onUploadProgress/onSettle.
 *                 This is what a stall guard needs: its timer is re-armed on every progress event.
 *                 A feature ends a request with `request.fail(reason, message)` rather than
 *                 aborting it, so the outcome reads as a transport failure, not a cancellation.
 *
 * `gateFirst` is deliberately not a feature: it is scheduling order, which belongs to the engine's
 * run loop, and it already lives in the `concurrency` block.
 *
 * Order in `createFeatures` is innermost first, which is what decides nesting when several
 * features wrap: retry sits closest to the request, so chunking will nest outside it and each
 * chunk will be retried on its own instead of restarting the whole file.
 *
 * Modes. `sendFiles(mode, …)` still takes the four mode words, and the three per-file ones now
 * resolve to the same call:
 *
 *   batch        sendBatchFiles      one request carrying every file
 *   parallel     sendUpload          concurrency.max, 4 by default
 *   limited      sendUpload          same, the word only ever meant "capped"
 *   sequential   sendUpload          concurrency.max = 1
 *
 * `sendParallelFiles` and `sendLimitedFiles` are kept as aliases of `sendUpload`.
 *
 * sendSequentialGroups composes the entry points rather than replacing them: it chains
 * independent groups (e.g. one per study) behind a gate, so the next group starts once
 * the current one completes — or, with `gateOn: 'firstFile'`, as soon as its first file
 * lands, overlapping groups while each group's first write settles server-side first.
 */

// ----------------------------------------------------------------------------

/*
 * Entry points. These build the shared context and the controller, so they are what
 * callers reach for.
 */
export { default as sendUpload } from './sendUpload';
export { default as sendBatchFiles } from './sendBatchFiles';
export { default as sendSequentialFiles } from './sendSequentialFiles';
export { default as sendSequentialGroups } from './sendSequentialGroups';
export { default as sendFiles } from './sendFiles';

/*
 * Previous names for `sendUpload`. Both were the same engine reached through a different mode
 * word — parallel took `concurrency`, limited took `concurrency ?? maxConcurrency` — and the
 * config resolver now handles both, so they are the same call. Kept so existing callers and the
 * docs page keep working; new code should use `sendUpload` with a `concurrency` block.
 */
export { default as sendLimitedFiles } from './sendUpload';
export { default as sendParallelFiles } from './sendUpload';

// ----------------------------------------------------------------------------

/*
 * Orchestrators. They run the files but create neither the context nor the controller:
 * each expects a TypeUploadContext already built by an entry point above, so they are
 * not usable on their own. Exported for backward compatibility.
 */
export { default as uploadFiles } from './uploadFiles';
export { default as uploadBatchFiles } from './uploadBatchFiles';
export { default as uploadSequentialFiles } from './uploadSequentialFiles';

// Previous names for the one per-file engine, kept for backward compatibility.
export { default as uploadLimitedFiles } from './uploadFiles';
export { default as uploadParallelFiles } from './uploadFiles';

// ----------------------------------------------------------------------------

/*
 * Feature layer. Exported so a caller can add one of their own: build a TypeUploadFeature and it
 * composes with the built-in ones through the same two extension points.
 */
export type { TypeAttemptFn, TypeLiveRequest, TypeRequestHooks, TypeUploadFeature } from './core/types';

export { default as composeAttempt } from './core/composeAttempt';
export { default as resolveUploadConfig } from './core/resolveUploadConfig';
export { default as sendRequest } from './core/sendRequest';
export { default as shouldSend } from './core/shouldSend';
export { default as createFeatures } from './features';
export { createFilterFeature, createRetryFeature, createStallFeature } from './features';
